import { Box, CircularProgress, Typography } from '@mui/material';
import Spinner from 'components/Spinner';
import UseAuthContext from 'context/AuthContext';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { saveProject } from 'services/project-service';
import { getFeatures, prepareRevision, runRevision } from 'services/revisions';
import { IProjectAddRequest, IRevisionCreate } from 'utils/contracts';
import { USER_ROLES } from 'utils/constants';
import { randomObjectId } from 'utils/Formatter';
import { createAndParseRevision } from 'utils/idfParse';
import {
  clearDraft,
  loadDraft,
} from 'utils/publicAnalyzeDraft';
import { getErrorHtmlContent, getErrorMessage } from 'utils/utils';

type Step = 'loading' | 'creating' | 'revising' | 'preparing' | 'running' | 'done' | 'error';

/** Same rule as NewProject / server isOrganizationSkipOverwriteAllowed. */
const resolveSkipOverwrite = (
  draftSkip: boolean,
  features: {
    skipOverwriteAllowed?: boolean;
    skipOverwriteAllowedStartDate?: string | Date | null;
  } | null | undefined,
  role: string | undefined
): boolean => {
  if (role === USER_ROLES.ADMIN) {
    return draftSkip;
  }
  let forced = !!features?.skipOverwriteAllowed;
  if (forced && features?.skipOverwriteAllowedStartDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(features.skipOverwriteAllowedStartDate);
    startDate.setHours(0, 0, 0, 0);
    forced = today >= startDate;
  }
  return forced ? true : draftSkip;
};

const PublicAnalyzeResumePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated, organizationId, role } = useContext(UseAuthContext);
  const [step, setStep] = useState<Step>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || started.current) return;
    started.current = true;

    (async () => {
      try {
        const { meta, file } = await loadDraft();
        if (!meta || meta.pendingAction !== 'createAndRun' || !file) {
          await clearDraft();
          setStep('error');
          setErrorMessage(t('PUBLIC_ANALYZE_RESUME_MISSING'));
          return;
        }
        if (!organizationId) {
          setStep('error');
          setErrorMessage(t('PUBLIC_ANALYZE_RESUME_NO_ORG'));
          return;
        }

        const projectType = meta.type || 'Residential';
        const projectId = randomObjectId();
        const now = new Date();
        const stamp = [
          String(now.getHours()).padStart(2, '0'),
          String(now.getMinutes()).padStart(2, '0'),
          String(now.getSeconds()).padStart(2, '0'),
        ].join('');
        const projectName = `New_${stamp}`;
        const draftSkip = meta.skip_overwrite !== false;
        let features = null;
        try {
          features = await getFeatures();
        } catch {
          // Fall through — still try create with draft value
        }
        const skipOverwrite = resolveSkipOverwrite(draftSkip, features, role);

        setStep('creating');
        const projectPayload: IProjectAddRequest = {
          id: projectId,
          name: projectName,
          client_name: '',
          type: projectType,
          climate: meta.climate,
          terrain: meta.terrain || 'city',
          city: '',
          address: '',
          gush: '',
          partition: '',
          version: '4.x.x',
          construction: 'new',
          skip_overwrite: skipOverwrite,
          single_block_per_floor: meta.single_block_per_floor !== false,
          organizationId,
          bg_color: '#808080',
        };
        const projectRes = await saveProject(projectPayload);
        if (projectRes.status !== 200) {
          throw new Error('PROJECT_CREATE_FAILED');
        }

        setStep('revising');
        const revisionPayload: IRevisionCreate = {
          file,
          notes: '',
          skip_overwrite: skipOverwrite,
          // Defaults match NewRevision form (required by revision.validator)
          h_report_density: '101',
          h_report_percentage: 15,
          h_report_uvalue: 2,
        };
        const parseState = await createAndParseRevision({
          file,
          projectId,
          organizationId,
          payload: revisionPayload,
        });
        const revisionId = parseState.revisionId;
        if (!revisionId) {
          throw new Error('REVISION_CREATE_FAILED');
        }

        // IDF is on the server — clear local draft ASAP
        await clearDraft();

        setStep('preparing');
        try {
          await prepareRevision(revisionId, projectId, organizationId, {
            idfVersion: parseState.sniffedVersion,
          });
        } catch {
          // prepare may fail if already prepared; continue to run
        }

        setStep('running');
        await runRevision(projectId, revisionId, organizationId);

        setStep('done');
        navigate(`/project/${projectId}/board`, { replace: true });
      } catch (error: any) {
        setStep('error');
        setErrorMessage(
          getErrorMessage(error, t('PUBLIC_ANALYZE_RESUME_FAILED'), t)
        );
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    })();
  }, [isAuthenticated, organizationId, role, enqueueSnackbar, navigate, t]);

  if (!isAuthenticated) {
    return <Navigate to="/login?redirect=/try/resume" replace />;
  }

  if (step === 'loading' || step === 'creating' || step === 'revising' || step === 'preparing' || step === 'running') {
    const label =
      step === 'creating'
        ? t('PUBLIC_ANALYZE_STEP_CREATE')
        : step === 'revising'
          ? t('PUBLIC_ANALYZE_STEP_REVISION')
          : step === 'preparing'
            ? t('PUBLIC_ANALYZE_STEP_PREPARE')
            : step === 'running'
              ? t('PUBLIC_ANALYZE_STEP_RUN')
              : t('PUBLIC_ANALYZE_STEP_LOADING');

    return (
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography>{label}</Typography>
      </Box>
    );
  }

  if (step === 'error') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
        <Typography
          component="a"
          href="/try"
          sx={{ color: 'primary.main', cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            navigate('/try');
          }}
        >
          {t('PUBLIC_ANALYZE_START_AGAIN')}
        </Typography>
      </Box>
    );
  }

  return <Spinner />;
};

export default PublicAnalyzeResumePage;
